import { Slot } from '@radix-ui/react-slot'
import {
  createContext,
  forwardRef,
  type RefObject,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react'

import { mergeRefs } from 'utils/ref_utils'

type BubblesProp = boolean | {
  escapeKey?: boolean;
  focusOutside?: boolean;
  pointerOutside?: boolean;
}

type Layer = {
  bubbles: {
    onEscapeKey?: boolean;
    onFocusOutside?: boolean;
    onPointerOutside?: boolean;
  };
  element: RefObject<HTMLElement>;
  handlers: {
    onEscapeKey?: (event: KeyboardEvent) => void;
    onFocusOutside?: (event: FocusEvent) => void;
    onPointerOutside?: (event: MouseEvent) => void;
  }
}

interface DismissibleLayerContextType {
  hasParent: boolean;
  layers: Map<string, Layer>;
}

const DismissibleLayerContext = createContext<DismissibleLayerContextType>({
  hasParent: false,
  layers: new Map<string, Layer>(),
})

/**
 * A hook that returns a `Map` of all of the layers in the current tree.
 * If the current layer is root of the tree, it will create and own the `Map`.
 * If it is not the root, it will pass along the `Map` from the parent.
 */
function useLayers () {
  const { hasParent, layers: parentLayers } = useContext(
    DismissibleLayerContext
  )
  const layers = useRef<Map<string, Layer>>()

  if (!layers.current) {
    if (hasParent) {
      layers.current = parentLayers
    } else {
      layers.current = new Map()
    }
  }

  return layers.current
}

/**
 * Registers a layer with the `Map` of all layers in the current tree.
 */
function useRegisteredLayer (layers: Map<string, Layer>, layer: Layer) {
  const layerId = useId()
  const layerAddedRef = useRef(false)

  /**
   * We cache the layer's properties (i.e. its configured handlers and bubbling
   * behavior) in a ref that gets updated on each render to avoid stale values
   * without causing effect hooks to rerun when they change.
  */
  const layerRef = useRef<Layer>(layer)
  Object.assign(layerRef.current, layer)

  /**
   * We register the layer in the component body instead of an effect hook
   * because we want the order of layers in the `Map` to reflect the order of
   * the layers in the tree. For example, for a React tree like this:
   *
   * ```
   * <DismissibleLayer key='a'>
   *   <DismissibleLayer key='b'>
   *     <DismissibleLayer key='c' />
   *   </DismissibleLayer>
   *  </DismissibleLayer>
   * ```
   *
   * Extracting an iterable array of layers should give us this order:
   *
   * ```
   * ['a', 'b', 'c']
   * ```
   *
   * Effect hooks are called from inside-out of the React tree, which would
   * register them in the reverse order.
   **/
  if (!layerAddedRef.current) {
    layerAddedRef.current = true
    layers.set(layerId, layerRef.current)
  }

  useEffect(() => {
    return () => {
      layers.delete(layerId)
    }
  }, [layers, layerId])
}

function expandBubblesProp (bubbles: BubblesProp) {
  if (typeof bubbles === 'boolean') {
    return {
      onEscapeKey: bubbles,
      onFocusOutside: bubbles,
      onPointerOutside: bubbles,
    }
  }

  return {
    onEscapeKey: !!bubbles.escapeKey,
    onFocusOutside: !!bubbles.focusOutside,
    onPointerOutside: !!bubbles.pointerOutside,
  }
}

/**
 * Iterates over the layers in reverse order and executes a callback with the
 * specified handler, following the layer's configured bubbling behavior.
 */
function iterateLayers<
  H extends keyof Layer['handlers'],
  E extends Parameters<NonNullable<Layer['handlers'][H]>>[0]
> (
  layers: Map<string, Layer>,
  handler: H,
  callback: ({
    element,
    handler,
  }: {
    element: HTMLElement | null;
    handler: (event: E) => void;
  }) => void
) {
  const reversedLayers = [...layers.values()].reverse()

  for (const layer of reversedLayers) {
    const handlerFn = layer.handlers[handler] as (event: E) => void
    callback({ element: layer.element.current, handler: handlerFn })

    if (!layer.bubbles[handler]) break
  }
}

interface DismissibleLayerProps extends React.HTMLAttributes<HTMLElement> {
  asChild?: boolean;
  bubbles?: BubblesProp;
  children?: React.ReactNode;
  onEscapeKey?: (event: KeyboardEvent) => void;
  onFocusOutside?: (event: FocusEvent) => void;
  onPointerOutside?: (event: MouseEvent) => void;
}

export const DismissibleLayer = forwardRef<HTMLDivElement, DismissibleLayerProps>(
  (
    {
      asChild,
      bubbles = false,
      children,
      onEscapeKey,
      onFocusOutside,
      onPointerOutside,
      ...props
    },
    forwardedRef
  ) => {
    const { hasParent } = useContext(DismissibleLayerContext)

    const elementRef = useRef<HTMLElement>(null)

    const layers = useLayers()
    const layerId = useRegisteredLayer(
      layers,
      {
        bubbles: expandBubblesProp(bubbles),
        element: elementRef,
        handlers: {
          onEscapeKey,
          onFocusOutside,
          onPointerOutside,
        },
      }
    )

    /**
     * This effect hook is responsible for handling keyboard, mouse, and focus
     * events that should trigger the dismissal of a layer. It attaches event
     * listeners to the root node in the tree and iterates over the callbacks
     * registered on each layer, according to each layer's configured
     * bubbling behavior.
     */
    useEffect(() => {
      if (hasParent) return

      function handleKeyDown (event: KeyboardEvent) {
        iterateLayers(layers, 'onEscapeKey', ({ handler }) => {
          if (event.key === 'Escape') {
            handler?.(event)
          }
        })
      }

      function handleMouseDown (event: MouseEvent) {
        iterateLayers(layers, 'onPointerOutside', ({ element, handler }) => {
          // Don't trigger on secondary clicks (i.e. right-click or ctrl-click)
          if (event.button === 2 || event.ctrlKey) return

          if (!element?.contains(event.target as Node)) {
            handler?.(event as MouseEvent)
          }
        })
      }

      function handleFocusIn (event: FocusEvent) {
        iterateLayers(layers, 'onFocusOutside', ({ element, handler }) => {
          if (!element?.contains(event.target as Node)) {
            handler?.(event)
          }
        })
      }

      document.addEventListener('keydown', handleKeyDown)
      document.addEventListener('mousedown', handleMouseDown)
      document.addEventListener('focusin', handleFocusIn)

      return () => {
        document.removeEventListener('keydown', handleKeyDown)
        document.removeEventListener('mousedown', handleMouseDown)
        document.removeEventListener('focusin', handleFocusIn)
      }
    }, [hasParent])

    const Component = asChild ? Slot : 'div'
    const contextValue = useMemo(() => ({ hasParent: true, layers }), [layers])

    return (
      <DismissibleLayerContext.Provider value={contextValue}>
        <Component
          {...props}
          data-layer-id={layerId}
          ref={mergeRefs(forwardedRef, elementRef)}
        >
          {children}
        </Component>
      </DismissibleLayerContext.Provider>
    )
  }
)
